import {
  isElementInViewport,
  isMindmapElementHidden,
  isMindmapNode,
} from "@excalidraw/element";

import { memoize, toBrandedType } from "@excalidraw/common";

import type {
  ExcalidrawElement,
  NonDeletedElementsMap,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";

import type { Scene } from "@excalidraw/element";

import { renderStaticSceneThrottled } from "../renderer/staticScene";

import type { RenderableElementsMap } from "./types";

import type { AppState } from "../types";

export class Renderer {
  private scene: Scene;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  public getRenderableElements = (() => {
    const getVisibleCanvasElements = ({
      elementsMap,
      zoom,
      offsetLeft,
      offsetTop,
      scrollX,
      scrollY,
      height,
      width,
    }: {
      elementsMap: NonDeletedElementsMap;
      zoom: AppState["zoom"];
      offsetLeft: AppState["offsetLeft"];
      offsetTop: AppState["offsetTop"];
      scrollX: AppState["scrollX"];
      scrollY: AppState["scrollY"];
      height: AppState["height"];
      width: AppState["width"];
    }): readonly NonDeletedExcalidrawElement[] => {
      const visibleElements: NonDeletedExcalidrawElement[] = [];
      for (const element of elementsMap.values()) {
        if (
          isElementInViewport(
            element,
            width,
            height,
            {
              zoom,
              offsetLeft,
              offsetTop,
              scrollX,
              scrollY,
            },
            elementsMap,
          )
        ) {
          visibleElements.push(element);
        }
      }
      return visibleElements;
    };

    const getRenderableElements = ({
      elements,
      editingTextElement,
      newElementId,
    }: {
      elements: readonly NonDeletedExcalidrawElement[];
      editingTextElement: AppState["editingTextElement"];
      newElementId: ExcalidrawElement["id"] | undefined;
    }) => {
      // Build a full map first for mind map parent traversal
      const fullMap = toBrandedType<NonDeletedElementsMap>(new Map());
      for (const element of elements) {
        fullMap.set(element.id, element);
      }

      // Collect all IDs that should be hidden due to mind map collapse.
      // Só esconde o que está na árvore LÓGICA (arrow tree) do ancestral
      // colapsado — e os elementos bound diretamente (text/arrow do node).
      // Nesting visual (containerParentId) e groupIds NÃO cascateiam
      // visibilidade: são relações espaciais/organizacionais, não lógicas.
      const hiddenIds = new Set<string>();
      for (const element of elements) {
        if (isMindmapElementHidden(element, fullMap)) {
          hiddenIds.add(element.id);
          if (element.boundElements) {
            for (const bound of element.boundElements) {
              hiddenIds.add(bound.id);
            }
          }
        }
      }

      // Geometric fallback: qualquer elemento cujo centro caia dentro do
      // bbox de um mindmap node escondido também some. Isso garante que
      // quando um node é colapsado, o que estiver visualmente dentro dele
      // desaparece junto, mesmo sem ter sido aninhado via Ctrl+G.
      const hiddenMindmapBoxes: Array<{
        x: number;
        y: number;
        x2: number;
        y2: number;
      }> = [];
      for (const id of hiddenIds) {
        const el = fullMap.get(id);
        if (el && isMindmapNode(el)) {
          hiddenMindmapBoxes.push({
            x: el.x,
            y: el.y,
            x2: el.x + el.width,
            y2: el.y + el.height,
          });
        }
      }
      if (hiddenMindmapBoxes.length > 0) {
        for (const element of elements) {
          if (hiddenIds.has(element.id)) {
            continue;
          }
          const cx = element.x + element.width / 2;
          const cy = element.y + element.height / 2;
          for (const box of hiddenMindmapBoxes) {
            if (cx >= box.x && cx <= box.x2 && cy >= box.y && cy <= box.y2) {
              hiddenIds.add(element.id);
              if (element.boundElements) {
                for (const bound of element.boundElements) {
                  hiddenIds.add(bound.id);
                }
              }
              break;
            }
          }
        }
      }

      const elementsMap = toBrandedType<RenderableElementsMap>(new Map());

      for (const element of elements) {
        if (newElementId === element.id) {
          continue;
        }

        // Skip all elements connected to collapsed mind map nodes
        if (hiddenIds.has(element.id)) {
          continue;
        }

        // we don't want to render text element that's being currently edited
        // (it's rendered on remote only)
        if (
          !editingTextElement ||
          editingTextElement.type !== "text" ||
          element.id !== editingTextElement.id
        ) {
          elementsMap.set(element.id, element);
        }
      }
      return elementsMap;
    };

    return memoize(
      ({
        zoom,
        offsetLeft,
        offsetTop,
        scrollX,
        scrollY,
        height,
        width,
        editingTextElement,
        newElementId,
        // cache-invalidation nonce
        sceneNonce: _sceneNonce,
      }: {
        zoom: AppState["zoom"];
        offsetLeft: AppState["offsetLeft"];
        offsetTop: AppState["offsetTop"];
        scrollX: AppState["scrollX"];
        scrollY: AppState["scrollY"];
        height: AppState["height"];
        width: AppState["width"];
        editingTextElement: AppState["editingTextElement"];
        /** note: first render of newElement will always bust the cache
         * (we'd have to prefilter elements outside of this function) */
        newElementId: ExcalidrawElement["id"] | undefined;
        sceneNonce: ReturnType<InstanceType<typeof Scene>["getSceneNonce"]>;
      }) => {
        const elements = this.scene.getNonDeletedElements();

        const elementsMap = getRenderableElements({
          elements,
          editingTextElement,
          newElementId,
        });

        const visibleElements = getVisibleCanvasElements({
          elementsMap,
          zoom,
          offsetLeft,
          offsetTop,
          scrollX,
          scrollY,
          height,
          width,
        });

        return { elementsMap, visibleElements };
      },
    );
  })();

  // NOTE Doesn't destroy everything (scene, rc, etc.) because it may not be
  // safe to break TS contract here (for upstream cases)
  public destroy() {
    renderStaticSceneThrottled.cancel();
    this.getRenderableElements.clear();
  }
}
